/**
 * React Query hooks for AI agents data fetching.
 */

import { useQuery, keepPreviousData, type UseQueryOptions } from "@tanstack/react-query";
import { listAIAgents } from "@/services/aiAgentsService";
import { useApiReady } from "@/hooks/useApi";
import type { Agent } from "@/types/security";

/**
 * Hook to fetch a list of AI agents with security issue statistics.
 * @param days - Number of days for trend calculation (default: 30)
 * @param organizationId - Optional org filter for MSSP tenant views
 */
export function useAIAgents(
  days: number = 30,
  organizationId?: number,
  options?: Omit<UseQueryOptions<Agent[], Error, Agent[], any[]>, "queryKey" | "queryFn">
) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["ai-agents", days, organizationId],
    queryFn: () => listAIAgents(days, organizationId),
    staleTime: 60000,
    placeholderData: keepPreviousData,
    enabled: isApiReady,
    ...options,
  });
}
